/**
 * MusicBed.jsx — the music layer, with ducking.
 *
 * Two sources: Rammy's own generated pads (always available, auditionable) or
 * a track you upload. Two sliders, because the user asked for control: how
 * loud the bed sits, and how far it dips while someone is speaking.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import api from "@/lib/api";
import * as L from "lucide-react";

const DEFAULTS = { volume: 0.22, duck: 0.7 };

const duckWord = (d) => (d < 0.35 ? "Barely dips" : d < 0.65 ? "Gentle" : d < 0.85 ? "Clear voice" : "Almost silent");

export function MusicBed({ music, onChange }) {
  const enabled = !!music;
  const source = music?.source || "library";
  const volume = music?.volume ?? DEFAULTS.volume;
  const duck = music?.duck ?? DEFAULTS.duck;

  const [beds, setBeds] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [auditioning, setAuditioning] = useState(null);
  const [uploading, setUploading] = useState(false);
  const audioRef = useRef(null);
  const fileRef = useRef(null);

  const loadTracks = () => api.get("/studio/music/tracks")
    .then(r => setTracks(r.data?.tracks || []))
    .catch(() => {});

  useEffect(() => {
    api.get("/studio/music/library").then(r => setBeds(r.data?.beds || [])).catch(() => {});
    loadTracks();
    return () => { try { audioRef.current?.pause(); } catch (_) { /* ignore */ } };
  }, []);

  const toggle = (on) => {
    if (!on) { onChange(null); return; }
    onChange({ source: "library", slug: beds[0]?.slug || "warm", track_id: null, ...DEFAULTS });
  };

  const audition = async (key, url) => {
    try { audioRef.current?.pause(); } catch (_) { /* ignore */ }
    if (auditioning === key) { setAuditioning(null); return; }
    setAuditioning(key);
    try {
      const res = await api.get(url, { responseType: "blob", timeout: 120000 });
      const objUrl = URL.createObjectURL(res.data);
      const el = new Audio(objUrl);
      el.loop = true;
      el.volume = 0.7;
      audioRef.current = el;
      el.addEventListener("pause", () => setTimeout(() => URL.revokeObjectURL(objUrl), 2000));
      await el.play();
    } catch (_) {
      setAuditioning(null);
      toast.error("Couldn't play that");
    }
  };

  const upload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("Music tracks must be under 20 MB"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("title", file.name.replace(/\.[^.]+$/, ""));
      const { data } = await api.post("/studio/music/tracks", form, { timeout: 240000 });
      await loadTracks();
      onChange({ source: "upload", slug: null, track_id: data.id, volume, duck });
      toast.success(`"${data.title}" added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload that track");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeTrack = async (t) => {
    try {
      await api.delete(`/studio/music/tracks/${t.id}`);
      setTracks(prev => prev.filter(x => x.id !== t.id));
      if (music?.track_id === t.id) {
        onChange({ source: "library", slug: beds[0]?.slug || "warm", track_id: null, volume, duck });
      }
      toast.success("Track removed");
    } catch (_) {
      toast.error("Couldn't remove that track");
    }
  };

  return (
    <div className="rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-4" data-testid="studio-music">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <L.Music size={14} className="text-[var(--rammy-cyan-dark)] shrink-0"/>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text)]">Music bed</div>
          <span className="text-[11px] text-[var(--rammy-text-muted)] truncate">
            Dips under the voice, swells in the pauses.
          </span>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
          <span className="text-[11px] text-[var(--rammy-text-sec)]">{enabled ? "On" : "Off"}</span>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={() => toggle(!enabled)}
            data-testid="studio-music-toggle"
            className={`w-9 h-5 rounded-full transition-colors relative ${enabled ? "bg-[var(--rammy-cyan-dark)]" : "bg-[var(--rammy-border)]"}`}
          >
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-[var(--rm-surface)] shadow transition-all ${enabled ? "left-[18px]" : "left-0.5"}`}/>
          </button>
        </label>
      </div>

      {enabled && (
        <div className="mt-3 space-y-3">
          <div className="inline-flex rounded-full bg-[var(--rammy-surface-alt)] p-1" data-testid="studio-music-source">
            {[
              { id: "library", label: "Rammy's beds" },
              { id: "upload", label: "Your music" },
            ].map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => onChange(opt.id === "library"
                  ? { source: "library", slug: music?.slug || beds[0]?.slug || "warm", track_id: null, volume, duck }
                  : { source: "upload", slug: null, track_id: music?.track_id || tracks[0]?.id || null, volume, duck })}
                data-active={source === opt.id ? "true" : "false"}
                data-testid={`studio-music-source-${opt.id}`}
                className={`px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${
                  source === opt.id ? "bg-[var(--rm-surface)] text-[var(--rammy-text)] shadow-sm" : "text-[var(--rammy-text-sec)]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {source === "library" ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="studio-music-library">
              {beds.map(bed => {
                const active = music?.slug === bed.slug;
                return (
                  <div
                    key={bed.slug}
                    className={`rounded-xl border p-2.5 transition-colors ${
                      active
                        ? "border-[var(--rammy-cyan-dark)] bg-[var(--rammy-cyan)]/12"
                        : "border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)]"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        onClick={() => onChange({ source: "library", slug: bed.slug, track_id: null, volume, duck })}
                        className="text-left flex-1 min-w-0"
                        data-testid={`studio-music-pick-${bed.slug}`}
                      >
                        <div className="text-[12.5px] font-medium truncate">{bed.label}</div>
                        <div className="text-[10.5px] text-[var(--rammy-text-muted)] truncate">{bed.description}</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => audition(bed.slug, `/studio/music/library/${bed.slug}`)}
                        className="text-[var(--rammy-cyan-dark)] hover:text-[var(--rammy-text)] shrink-0 transition-colors"
                        data-testid={`studio-music-audition-${bed.slug}`}
                        aria-label={`Hear ${bed.label}`}
                      >
                        {auditioning === bed.slug ? <L.Pause size={13}/> : <L.Play size={13}/>}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2" data-testid="studio-music-uploads">
              {tracks.map(t => {
                const active = music?.track_id === t.id;
                return (
                  <div
                    key={t.id}
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2 transition-colors ${
                      active ? "border-[var(--rammy-cyan-dark)] bg-[var(--rammy-cyan)]/12" : "border-[var(--rammy-border)]"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => onChange({ source: "upload", slug: null, track_id: t.id, volume, duck })}
                      className="text-left flex-1 min-w-0"
                      data-testid={`studio-music-track-${t.id}`}
                    >
                      <div className="text-[12.5px] font-medium truncate">{t.title}</div>
                      <div className="text-[10.5px] text-[var(--rammy-text-muted)]">
                        {t.size ? `${(t.size / (1024 * 1024)).toFixed(1)} MB` : ""}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => audition(t.id, `/studio/music/tracks/${t.id}/file`)}
                      className="text-[var(--rammy-cyan-dark)] hover:text-[var(--rammy-text)] p-1"
                      data-testid={`studio-music-audition-track-${t.id}`}
                      aria-label="Play track"
                    >
                      {auditioning === t.id ? <L.Pause size={13}/> : <L.Play size={13}/>}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeTrack(t)}
                      className="text-[var(--rammy-text-muted)] hover:text-red-500 p-1"
                      data-testid={`studio-music-remove-${t.id}`}
                      aria-label="Remove track"
                    >
                      <L.Trash2 size={13}/>
                    </button>
                  </div>
                );
              })}
              <input
                ref={fileRef}
                type="file"
                accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/ogg"
                className="hidden"
                onChange={(e) => upload(e.target.files)}
                data-testid="studio-music-file-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full rounded-xl border border-dashed border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] py-2.5 text-[12px] text-[var(--rammy-text-sec)] inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                data-testid="studio-music-upload"
              >
                {uploading ? <L.Loader2 size={13} className="animate-spin"/> : <L.Upload size={13}/>}
                {uploading ? "Uploading…" : "Upload a track (MP3 or WAV, under 20 MB)"}
              </button>
              {!tracks.length && !uploading && (
                <div className="text-[10.5px] text-[var(--rammy-text-muted)]">
                  Use music you own or that's licensed for your use.
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div className="flex items-center gap-2">
              <span className="text-[10.5px] uppercase tracking-widest text-[var(--rammy-text-muted)] w-14 shrink-0">Level</span>
              <Slider
                min={0}
                max={80}
                step={1}
                value={[Math.round(volume * 100)]}
                onValueChange={([v]) => onChange({ ...music, volume: v / 100 })}
                className="flex-1"
                data-testid="studio-music-volume"
              />
              <span className="text-[11px] text-[var(--rammy-text-sec)] w-9 text-right">{Math.round(volume * 100)}%</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10.5px] uppercase tracking-widest text-[var(--rammy-text-muted)] w-14 shrink-0">Duck</span>
              <Slider
                min={0}
                max={100}
                step={5}
                value={[Math.round(duck * 100)]}
                onValueChange={([v]) => onChange({ ...music, duck: v / 100 })}
                className="flex-1"
                data-testid="studio-music-duck"
              />
              <span className="text-[11px] text-[var(--rammy-text-sec)] w-[70px] text-right truncate" data-testid="studio-music-duck-label">
                {duckWord(duck)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
